<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>通知群配置</h1>
        <p>纸条审核、动态复核、举报、学生认证这些待办，以机器人消息推到各校钉钉群</p>
      </div>
      <div class="head-actions">
        <el-button class="ghost-btn" icon="el-icon-refresh" @click="fetchData">刷新</el-button>
        <el-button type="primary" class="primary-btn" icon="el-icon-plus" @click="handleCreate">
          新增通知群
        </el-button>
      </div>
    </div>

    <!-- ── 状态提示：这几条都会直接影响"群里能不能收到" ─────── -->
    <div v-if="!enabled" class="hint-bar hint-warn">
      <i class="el-icon-warning-outline" />
      <div>
        <b>通知总开关是关的</b>，现在群里收不到任何消息（待办事件仍在落库，不会丢）。
        去「系统配置 → 通知」把 <code>notice.enabled</code> 改成 1 即可生效，不用重启。
      </div>
    </div>

    <div v-if="testMode" class="hint-bar hint-info">
      <i class="el-icon-info" />
      <div>
        <b>测试模式已开启</b>：所有通知只会发到「全域兜底群」，各校运营收不到。
        上线前用它验格式和频率，验完记得去系统配置把 <code>notice.test_mode</code> 改回 0。
      </div>
    </div>

    <div v-if="!hasFallback" class="hint-bar hint-danger">
      <i class="el-icon-warning" />
      <div>
        <b>还没有全域兜底群</b>：系统级提醒（超时升级、链路异常）无处可发；
        尚未配群的学校，其待办也会因为找不到落脚点而丢失。建议先建一个。
      </div>
    </div>

    <div v-if="missingSchoolCount > 0" class="hint-bar hint-warn">
      <i class="el-icon-s-opportunity" />
      <div>
        <b>{{ missingSchoolCount }} 所学校尚未配置通知群</b>，这些学校的待办会全部转进兜底群，
        超管群会越来越吵。{{ missingSchoolNames.length ? '（' + missingSchoolNames.join('、') + (missingSchoolCount > missingSchoolNames.length ? '…' : '') + '）' : '' }}
      </div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="loading" :data="groups" style="width: 100%">
        <el-table-column label="群名称" min-width="220">
          <template slot-scope="{ row }">
            <div class="ng-name">
              <span class="ng-name__t">{{ row.name }}</span>
              <span v-if="row.fallback" class="tag is-rose">兜底</span>
            </div>
          </template>
        </el-table-column>

        <!-- 这一列是信息重心：哪个群对应哪所学校，扫一眼必须分清。
             它同时就是权限边界 —— 兰州大学的群只收兰大的消息。 -->
        <el-table-column label="归属学校" min-width="180">
          <template slot-scope="{ row }">
            <span v-if="row.fallback" class="ng-scope ng-scope--all">全站兜底（只给超管）</span>
            <span v-else class="ng-scope">{{ row.schoolName }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Webhook" min-width="260">
          <template slot-scope="{ row }">
            <span class="ng-mono">{{ row.webhookMasked }}</span>
          </template>
        </el-table-column>

        <el-table-column label="加签" width="90" align="center">
          <template slot-scope="{ row }">
            <span class="ng-sub">{{ row.signed ? '已配' : '未配' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="120">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="row.enabled === 1 ? 's1' : 's0'">
              <i />{{ row.enabled === 1 ? '启用' : '已停用' }}
            </span>
          </template>
        </el-table-column>

        <!-- 最近发送 / 失败原因：出问题时这里就是第一现场 -->
        <el-table-column label="最近发送" width="180">
          <template slot-scope="{ row }">
            <span v-if="row.lastSentAt" class="ng-sub">{{ formatDate(row.lastSentAt) }}</span>
            <span v-else class="ng-sub is-muted">还没发过</span>
          </template>
        </el-table-column>

        <el-table-column label="异常" min-width="180">
          <template slot-scope="{ row }">
            <span v-if="row.lastError" class="ng-err" :title="row.lastError">
              连续失败 {{ row.failCount }} 次：{{ row.lastError }}
            </span>
            <span v-else class="ng-sub is-muted">—</span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <el-dropdown trigger="click" @command="cmd => handleAction(cmd, row)">
                <button type="button" class="act act-plain act-more">
                  操作<i class="el-icon-arrow-down" />
                </button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="test">
                    <i class="el-icon-position" /> 发送测试
                  </el-dropdown-item>
                  <el-dropdown-item command="edit">
                    <i class="el-icon-edit" /> 编辑
                  </el-dropdown-item>
                  <el-dropdown-item :command="row.enabled === 1 ? 'disable' : 'enable'">
                    <i :class="row.enabled === 1 ? 'el-icon-close' : 'el-icon-check'" />
                    {{ row.enabled === 1 ? '停用' : '启用' }}
                  </el-dropdown-item>
                  <!-- 兜底群不给删除入口：删了之后"没配群的学校"就无处可去 -->
                  <el-dropdown-item v-if="!row.fallback" command="delete" divided>
                    <i class="el-icon-delete" /> 删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>

        <template slot="empty">
          <div class="ng-empty">
            <i class="el-icon-bell" />
            <p>还没有配置任何通知群</p>
            <el-button size="small" type="primary" class="primary-btn" @click="handleCreate">
              先建一个兜底群
            </el-button>
          </div>
        </template>
      </el-table>
    </div>

    <!-- ── 新增 / 编辑 ─────────────────────────────────────── -->
    <el-drawer
      :visible.sync="drawerVisible"
      direction="rtl"
      size="560px"
      :with-header="false"
      custom-class="admin-drawer"
      @closed="onDrawerClosed"
    >
      <div class="drawer-inner">
        <div class="drawer-head">
          <div class="dh-left">
            <span class="dh-id">{{ isEdit ? '编辑通知群' : '新增通知群' }}</span>
          </div>
          <button type="button" class="dh-close" @click="drawerVisible = false">
            <i class="el-icon-close" />
          </button>
        </div>

        <div class="drawer-body">
          <el-form ref="form" :model="form" :rules="rules" label-width="96px">
            <el-form-item label="群名称" prop="name">
              <el-input v-model="form.name" maxlength="64" show-word-limit
                        placeholder="如：【纸条盲盒】兰州大学 · 审核通知" />
              <div class="ng-note">建议带上学校名。群里出现多所学校时，靠群名分辨是最快的。</div>
            </el-form-item>

            <el-form-item label="归属学校" prop="schoolId">
              <!-- ⛔ 归属学校决定"这个群能看见谁的消息"，改它等于搬动权限边界，
                   所以编辑时不允许改（要换学校就新建一个群）。 -->
              <el-select
                v-model="form.schoolId"
                :disabled="isEdit"
                placeholder="请选择学校"
                style="width: 100%"
                filterable
              >
                <el-option label="全域兜底（只给超管）" :value="0" />
                <el-option
                  v-for="item in schoolOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
              <div class="ng-note">
                <template v-if="isEdit">归属不可修改。要给另一所学校配群，请单独新增一个。</template>
                <template v-else>
                  一个学校一个群 —— 钉钉机器人没有"只给某人看"的能力，群就是权限边界。
                </template>
              </div>
            </el-form-item>

            <el-form-item label="Webhook" prop="webhookUrl">
              <el-input
                v-model="form.webhookUrl"
                type="textarea"
                :rows="2"
                placeholder="https://oapi.dingtalk.com/robot/send?access_token=…"
              />
              <div class="ng-note">
                钉钉群 → 群设置 → 智能群助手 → 添加机器人 → 自定义。复制完整地址粘贴到这里。
                <template v-if="isEdit">不改就保持原样，不会把已保存的地址覆盖掉。</template>
              </div>
            </el-form-item>

            <el-form-item label="加签密钥">
              <el-input
                v-model="form.signSecret"
                placeholder="SEC 开头；机器人安全设置选「加签」时才需要"
                show-password
              />
              <div class="ng-note">
                机器人安全设置二选一即可：选「自定义关键词」时必须填 <b>纸条盲盒</b>；
                选「加签」则把这里的密钥填进来。
              </div>
            </el-form-item>

            <el-form-item label="催办手机号">
              <el-input v-model="form.atMobiles" placeholder="多个用逗号分隔，如 13800138000,13900139000" />
              <div class="ng-note">
                待办超时催办时会 @ 这些人。留空则只发消息、不 @ 任何人。
              </div>
            </el-form-item>

            <el-form-item label="启用">
              <el-switch v-model="form.enabled" :active-value="1" :inactive-value="0" />
            </el-form-item>
          </el-form>

          <!-- 测试结果就地展示，不弹 toast —— 错误码要能被读两遍 -->
          <div v-if="testResult" class="ng-test" :class="testResult.ok ? 'is-ok' : 'is-bad'">
            <i :class="testResult.ok ? 'el-icon-success' : 'el-icon-error'" />
            <div>{{ testResult.text }}</div>
          </div>
        </div>

        <div class="drawer-foot">
          <el-button class="ghost-btn" :loading="testing" @click="handleTest">发送测试</el-button>
          <div class="foot-spacer" />
          <el-button class="ghost-btn" @click="drawerVisible = false">取消</el-button>
          <el-button type="primary" class="primary-btn" :loading="submitting" @click="submitForm">
            保存
          </el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  getNoticeGroupOverview,
  createNoticeGroup,
  updateNoticeGroup,
  toggleNoticeGroup,
  deleteNoticeGroup,
  testNoticeGroup
} from '@/api/notice-group'

export default {
  name: 'NoticeGroup',
  data() {
    return {
      loading: false,
      submitting: false,
      testing: false,
      groups: [],
      enabled: false,
      testMode: false,
      hasFallback: false,
      missingSchoolCount: 0,
      missingSchoolNames: [],

      drawerVisible: false,
      isEdit: false,
      testResult: null,
      form: this.emptyForm(),
      rules: {
        name: [{ required: true, message: '请填写群名称', trigger: 'blur' }],
        schoolId: [{ required: true, message: '请选择归属学校', trigger: 'change' }],
        webhookUrl: [{ required: true, message: '请填写 Webhook 地址', trigger: 'blur' }]
      }
    }
  },
  computed: {
    ...mapGetters(['isSuperAdmin', 'schoolOptions'])
  },
  created() {
    this.fetchData()
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: '',
        schoolId: 0,
        webhookUrl: '',
        signSecret: '',
        atMobiles: '',
        enabled: 1
      }
    },

    fetchData() {
      this.loading = true
      getNoticeGroupOverview()
        .then(res => {
          const data = res.data || {}
          this.groups = data.groups || []
          this.enabled = !!data.enabled
          this.testMode = !!data.testMode
          this.hasFallback = !!data.hasFallback
          this.missingSchoolCount = data.missingSchoolCount || 0
          this.missingSchoolNames = data.missingSchoolNames || []
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    handleCreate() {
      this.isEdit = false
      this.form = this.emptyForm()
      // 已经有兜底群了，默认就让他选学校 —— 免得再建第二个，被后端挡回来
      this.form.schoolId = this.hasFallback ? undefined : 0
      this.testResult = null
      this.drawerVisible = true
    },

    handleEdit(row) {
      this.isEdit = true
      this.testResult = null
      this.form = {
        id: row.id,
        name: row.name,
        schoolId: row.schoolId,
        // 展示脱敏值；用户不改就原样回传，后端识别 **** 视为不修改
        webhookUrl: row.webhookMasked,
        signSecret: row.signed ? '****（已配置，留空或保持原样均不修改）' : '',
        atMobiles: row.atMobiles || '',
        enabled: row.enabled
      }
      this.drawerVisible = true
    },

    onDrawerClosed() {
      this.form = this.emptyForm()
      this.testResult = null
    },

    handleAction(cmd, row) {
      if (cmd === 'edit') return this.handleEdit(row)
      if (cmd === 'test') return this.testSaved(row)
      if (cmd === 'enable') return this.handleToggle(row)
      if (cmd === 'disable') return this.handleToggle(row)
      if (cmd === 'delete') return this.handleDelete(row)
    },

    handleToggle(row) {
      toggleNoticeGroup(row.id).then(res => {
        this.$message.success(res.message || '已更新')
        this.fetchData()
      }).catch(() => {})
    },

    handleDelete(row) {
      this.$confirm(
        '删除后这个群将收不到任何通知。确定删除「' + row.name + '」吗？',
        '删除通知群',
        { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }
      ).then(() => {
        deleteNoticeGroup(row.id).then(res => {
          this.$message.success(res.message || '已删除')
          this.fetchData()
        }).catch(() => {})
      }).catch(() => {})
    },

    /** 列表里直接测已保存的群 */
    testSaved(row) {
      this.testing = true
      testNoticeGroup({ id: row.id })
        .then(res => {
          this.testing = false
          this.$message.success(res.message || '测试消息已发出')
          this.fetchData()
        })
        .catch(err => {
          this.testing = false
          this.$message.error((err && err.message) || '发送失败')
        })
    },

    /** 抽屉里测：还没保存也能测，符合"先测再存"的直觉 */
    handleTest() {
      this.testResult = null
      this.testing = true
      const payload = {
        id: this.isEdit ? this.form.id : null,
        name: this.form.name,
        schoolId: this.form.schoolId,
        webhookUrl: this.form.webhookUrl,
        signSecret: this.form.signSecret,
        atMobiles: this.form.atMobiles
      }
      testNoticeGroup(payload)
        .then(res => {
          this.testing = false
          this.testResult = { ok: true, text: res.message || '测试消息已发出，请到群里确认' }
        })
        .catch(err => {
          this.testing = false
          this.testResult = {
            ok: false,
            text: (err && err.message) || '发送失败，请检查 Webhook 与加签设置'
          }
        })
    },

    submitForm() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.submitting = true
        const payload = {
          id: this.form.id,
          name: this.form.name,
          schoolId: this.form.schoolId,
          webhookUrl: this.form.webhookUrl,
          signSecret: this.form.signSecret,
          atMobiles: this.form.atMobiles,
          enabled: this.form.enabled
        }
        const request = this.isEdit ? updateNoticeGroup(payload) : createNoticeGroup(payload)
        request
          .then(res => {
            this.submitting = false
            this.drawerVisible = false
            this.$message.success(res.message || '已保存')
            this.fetchData()
          })
          .catch(err => {
            this.submitting = false
            this.$message.error((err && err.message) || '保存失败')
          })
      })
    },

    formatDate(value) {
      if (!value) return ''
      return String(value).slice(0, 16)
    }
  }
}
</script>

<style lang="scss" scoped>
.ng-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ng-name__t {
  font-weight: 600;
  color: var(--ink-900);
}

/* 归属学校是这一列的信息重心，给它一点重量 */
.ng-scope {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--ink-100);
  color: var(--ink-800);
  font-size: 12px;
}

.ng-scope--all {
  background: var(--rose-50);
  color: var(--rose-600);
}

.ng-mono {
  font-size: 12px;
  letter-spacing: 0.2px;
  color: var(--ink-500);
  word-break: break-all;
}

.ng-sub {
  font-size: 12px;
  color: var(--ink-500);
}

.ng-sub.is-muted {
  color: var(--ink-300);
}

.ng-err {
  font-size: 12px;
  color: var(--rose-600);
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ng-note {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.7;
  color: var(--ink-400);
}

.ng-empty {
  padding: 36px 0;
  text-align: center;
  color: var(--ink-400);
}

.ng-empty i {
  font-size: 26px;
  display: block;
  margin-bottom: 8px;
}

.ng-empty p {
  margin: 0 0 14px;
  font-size: 13px;
}

.ng-test {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--r-md);
  font-size: 13px;
  line-height: 1.7;
}

.ng-test.is-ok {
  background: var(--ok-bg);
  color: var(--ok-text);
}

.ng-test.is-bad {
  background: var(--danger-bg);
  color: var(--danger);
}

.ng-test i {
  font-size: 16px;
  line-height: 1.4;
}

.hint-bar {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: var(--r-md);
  font-size: 13px;
  line-height: 1.7;
}

.hint-bar i {
  font-size: 16px;
  margin-top: 1px;
}

.hint-bar code {
  background: rgba(0, 0, 0, 0.05);
  padding: 1px 5px;
  border-radius: 4px;
}

.hint-warn {
  background: var(--warn-bg);
  color: var(--warn-text);
}

.hint-info {
  background: var(--ink-100);
  color: var(--ink-500);
}

.hint-danger {
  background: var(--danger-bg);
  color: var(--danger);
}
</style>
